import { useEffect, useState } from 'react';
import { ERROR_MESSAGE } from 'appConstants';
import { NewSubscriptionApi } from 'api/newSubscriptionApi';
import { buildOptionsGrid } from 'subscriptionOptions/optionsGrid';
import { AccountContext, OptionsGridModel, OptionsResponse, OptionsType } from 'subscriptionOptions/types';

type FetchOptionsResult = {
  grid: OptionsGridModel | null;
  accountContext: AccountContext | null;
  loading: boolean;
  error: string;
};

/** Responses keyed by options type and subject list, so a regroup does not refetch or flicker. Pass one per page. */
export type OptionsCache = Map<string, OptionsResponse>;

export const createOptionsCache = (): OptionsCache => new Map();

type FetchOptionsConfig = {
  /** Subjects to ask about; empty means every subject the school may pick. */
  subjectsIDs?: number[];
  cache?: OptionsCache;
};

/** Loads the school's selectable packages for one options type, unanchored. */
export const useFetchOptions = (
  optionsType: OptionsType,
  enabled: boolean = true,
  config: FetchOptionsConfig = {}
): FetchOptionsResult => {
  const subjectsIDs = config.subjectsIDs || [];
  const cache = config.cache;
  const cacheKey = optionsType + ':' + [...subjectsIDs].sort((a, b) => a - b).join(',');

  const cached = cache ? cache.get(cacheKey) : undefined;

  const [grid, setGrid] = useState<OptionsGridModel | null>(cached ? buildOptionsGrid(cached) : null);
  const [accountContext, setAccountContext] = useState<AccountContext | null>(cached ? cached.accountContext : null);
  const [loading, setLoading] = useState<boolean>(enabled && !cached);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const hit = cache ? cache.get(cacheKey) : undefined;
    if (hit) {
      setGrid(buildOptionsGrid(hit));
      setAccountContext(hit.accountContext);
      setError('');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    NewSubscriptionApi.getOptions({
      packagesPromos: [],
      subjectsIDs,
      optionsType,
    }).then((result) => {
      if (cancelled) {
        return;
      }

      if (result.data) {
        if (cache) {
          cache.set(cacheKey, result.data);
        }
        setGrid(buildOptionsGrid(result.data));
        setAccountContext(result.data.accountContext);
      } else {
        setGrid(null);
        setAccountContext(null);
        setError((result.error && result.error.message) || ERROR_MESSAGE);
      }

      setLoading(false);
    }).catch(() => {
      if (cancelled) {
        return;
      }

      setGrid(null);
      setAccountContext(null);
      setError(ERROR_MESSAGE);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // subjectsIDs is folded into cacheKey; a new array with the same ids must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsType, enabled, cacheKey, cache]);

  return { grid, accountContext, loading, error };
};
